import React, { useEffect, useState, createContext, useContext, lazy } from 'react';
import { HomePage } from './pages/HomePage';
import { ProjectPage } from './pages/ProjectPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { AdminPage } from './pages/AdminPage';
import { LoginPage } from './pages/LoginPage';
import { LoadingScreen } from './components/LoadingScreen';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { supabase, getImageUrl } from './lib/supabase';
import { cache, CACHE_KEYS } from './lib/cache';
import { imageCache } from './lib/imageCache';
import type { Project, ProjectListItem, ProjectsPageItem } from './lib/supabase';
// Theme context
interface ThemeContextType {
  theme: 'dark' | 'light';
  toggleTheme: () => void;
}
export const ThemeContext = createContext<ThemeContextType>({
  theme: 'dark',
  toggleTheme: () => {}
});
export const useTheme = () => useContext(ThemeContext);
// Load theme from localStorage
const loadTheme = (): 'dark' | 'light' => {
  try {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme;
    }
  } catch (error) {
    console.error('Error loading theme from localStorage:', error);
  }
  return 'dark';
};
function AppContent() {
  const [currentView, setCurrentView] = useState<'home' | 'project' | 'projects' | 'admin'>('home');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [projectsPageData, setProjectsPageData] = useState<ProjectsPageItem[]>([]);
  const [theme, setTheme] = useState<'dark' | 'light'>(loadTheme);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingProjectsPage, setIsLoadingProjectsPage] = useState(false);
  const [isLoadingProjectDetail, setIsLoadingProjectDetail] = useState(false);
  const {
    user,
    loading: authLoading
  } = useAuth();
  // Save theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.classList.toggle('light', theme === 'light');
  }, [theme]);
  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };
  // Load projects LIST (lightweight, no images) - OPTIMIZED QUERY
  const loadProjects = async () => {
    setIsLoadingProjects(true);
    try {
      // ALWAYS fetch fresh data, don't use cache for now to debug
      console.log('Loading projects from database...');
      // OPTIMIZED: Only fetch lightweight fields, NO image data
      const {
        data,
        error
      } = await supabase.from('projects').select('id, title, category, year, thumb_path, image_path, media_type, position').order('position', {
        ascending: true
      });
      if (error) throw error;
      console.log('Projects loaded:', data);
      if (data) {
        setProjects(data as ProjectListItem[]);
        cache.set(CACHE_KEYS.PROJECTS, data);
        // Preload thumbnail URLs
        const thumbnailUrls = data.map(p => {
          // Use thumb_path if available, otherwise use image_path
          const path = p.thumb_path || p.image_path;
          return getImageUrl(path);
        }).filter(Boolean) as string[];
        console.log('Preloading thumbnails:', thumbnailUrls);
        imageCache.preloadBatch(thumbnailUrls);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setIsLoadingProjects(false);
    }
  };
  // Load FULL project details (only when clicking into detail view)
  const loadProjectDetail = async (projectId: string) => {
    setIsLoadingProjectDetail(true);
    try {
      const cacheKey = `${CACHE_KEYS.PROJECT_DETAIL}_${projectId}`;
      const cached = cache.get<Project>(cacheKey);
      if (cached) {
        setSelectedProject(cached);
        setIsLoadingProjectDetail(false);
        return;
      }
      const {
        data,
        error
      } = await supabase.from('projects').select('*').eq('id', projectId).single();
      if (error) throw error;
      if (data) {
        setSelectedProject(data as Project);
        cache.set(cacheKey, data);
        const imageUrl = getImageUrl(data.image_path);
        if (imageUrl) {
          imageCache.preload(imageUrl);
        }
      }
    } catch (error) {
      console.error('Error loading project detail:', error);
    } finally {
      setIsLoadingProjectDetail(false);
    }
  };
  // Load projects page data ONLY when needed (lazy load)
  const loadProjectsPageData = async () => {
    if (projectsPageData.length > 0) return;
    setIsLoadingProjectsPage(true);
    try {
      const cached = cache.get<ProjectsPageItem[]>(CACHE_KEYS.PROJECTS_PAGE);
      if (cached) {
        setProjectsPageData(cached);
        setIsLoadingProjectsPage(false);
        const imageUrls = cached.map(p => getImageUrl(p.image_path)).filter(Boolean) as string[];
        imageCache.preloadBatch(imageUrls);
        return;
      }
      const {
        data,
        error
      } = await supabase.from('projects_page').select('id, title, description, image_path, link, category, button_text, button_disabled, position').order('position', {
        ascending: true
      });
      if (error) throw error;
      if (data) {
        setProjectsPageData(data as ProjectsPageItem[]);
        cache.set(CACHE_KEYS.PROJECTS_PAGE, data);
        const imageUrls = data.map(p => getImageUrl(p.image_path)).filter(Boolean) as string[];
        imageCache.preloadBatch(imageUrls);
      }
    } catch (error) {
      console.error('Error loading projects page data:', error);
    } finally {
      setIsLoadingProjectsPage(false);
    }
  };
  // Initial data load - ONLY load work projects list
  useEffect(() => {
    const loadData = async () => {
      await imageCache.init();
      await loadProjects();
    };
    loadData();
  }, []);
  const handleProjectClick = async (project: ProjectListItem) => {
    setCurrentView('project');
    await loadProjectDetail(project.id);
  };
  const handleBackToHome = () => {
    setCurrentView('home');
    setSelectedProject(null);
  };
  const handleGoToAdmin = () => {
    setCurrentView('admin');
  };
  const handleGoToProjects = async () => {
    setCurrentView('projects');
    await loadProjectsPageData();
  };
  const handleBackFromAdmin = () => {
    setCurrentView('home');
    // Reload projects when coming back from admin
    loadProjects();
  };
  // Show login page if trying to access admin without auth
  if (currentView === 'admin' && !user) {
    return <ThemeContext.Provider value={{
      theme,
      toggleTheme
    }} data-id="element-0">
        <LoginPage data-id="element-1" />
      </ThemeContext.Provider>;
  }
  if (currentView === 'admin') {
    return <ThemeContext.Provider value={{
      theme,
      toggleTheme
    }} data-id="element-2">
        <AdminPage onUpdateProjects={async () => {
        console.log('Clearing cache and reloading projects...');
        cache.clear(CACHE_KEYS.PROJECTS);
        await loadProjects();
      }} onUpdateProjectsPageData={async () => {
        cache.clear(CACHE_KEYS.PROJECTS_PAGE);
        await loadProjectsPageData();
      }} onBack={handleBackFromAdmin} data-id="element-3" />
      </ThemeContext.Provider>;
  }
  if (currentView === 'projects') {
    return <ThemeContext.Provider value={{
      theme,
      toggleTheme
    }} data-id="element-4">
        <ProjectsPage projects={projectsPageData} onGoBack={handleBackToHome} onGoToAdmin={handleGoToAdmin} isLoading={isLoadingProjectsPage} data-id="element-5" />
      </ThemeContext.Provider>;
  }
  if (currentView === 'project' && selectedProject) {
    return <ThemeContext.Provider value={{
      theme,
      toggleTheme
    }} data-id="element-6">
        <ProjectPage project={selectedProject} onBack={handleBackToHome} onGoToAdmin={handleGoToAdmin} onGoToProjects={handleGoToProjects} isLoading={isLoadingProjectDetail} data-id="element-7" />
      </ThemeContext.Provider>;
  }
  return <ThemeContext.Provider value={{
    theme,
    toggleTheme
  }} data-id="element-8">
      <HomePage projects={projects} onProjectClick={handleProjectClick} onGoToAdmin={handleGoToAdmin} onGoToProjects={handleGoToProjects} isLoadingProjects={isLoadingProjects} data-id="element-9" />
    </ThemeContext.Provider>;
}
export function App() {
  return <AuthProvider data-id="element-10">
      <AppContent data-id="element-11" />
    </AuthProvider>;
}